import React, { useCallback, useState } from 'react';
import { Button, Card, Tab } from 'react-bootstrap';

import type {
  GridGetterFunction,
  GridCountFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useNavigation } from '../../../common/navigation';
import { InvoicePaymentsSearch } from '../../components/InvoicePayment/InvoicePaymentsSearch';
import { CsvImportSection } from '../../components/Common/CsvImportSection';
import { TabsHistory } from '../../../app/components/TabsHistory';

export const InvoicePaymentsView: React.FC = () => {
  const spd = useSpdCore();
  const nav = useNavigation();
  const [refreshKey, setRefreshKey] = useState(0);

  const getHandlingData: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.invoicePayments.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        undefined,
        true,
      ),
    [spd.invoicePayments],
  );

  const getHandlingCount: GridCountFunction = useCallback(
    (filterText?: string, filterColumns?: string) =>
      spd.invoicePayments.getAllCount(
        filterText,
        filterColumns,
        undefined,
        true,
      ),
    [spd.invoicePayments],
  );

  const getFullyLinkedData: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.invoicePayments.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        undefined,
        undefined,
        true,
      ),
    [spd.invoicePayments],
  );

  const getFullyLinkedCount: GridCountFunction = useCallback(
    (filterText?: string, filterColumns?: string) =>
      spd.invoicePayments.getAllCount(
        filterText,
        filterColumns,
        undefined,
        undefined,
        true,
      ),
    [spd.invoicePayments],
  );

  const getAllData: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.invoicePayments.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ),
    [spd.invoicePayments],
  );

  const getAllCount: GridCountFunction = useCallback(
    (filterText?: string, filterColumns?: string) =>
      spd.invoicePayments.getAllCount(filterText, filterColumns),
    [spd.invoicePayments],
  );

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>Wpłaty</span>
      </Card.Header>
      <Card.Body>
        <TabsHistory id="InvoicePayments" className="mb-3" activeKey="handling">
          <Tab eventKey="handling" title="Do obsługi">
            <InvoicePaymentsSearch
              getData={getHandlingData}
              getCount={getHandlingCount}
              showFilter={true}
              withFilterInColumns={true}
              onRowClick={(id: string) => nav.invoicePaymentEditor(id)}
              refresh={refreshKey > 0}
              showExport={true}
            />
          </Tab>
          <Tab eventKey="done" title="Dowiązane">
            <InvoicePaymentsSearch
              getData={getFullyLinkedData}
              getCount={getFullyLinkedCount}
              showFilter={true}
              withFilterInColumns={true}
              onRowClick={(id: string) => nav.invoicePaymentEditor(id)}
              refresh={refreshKey > 0}
            />
          </Tab>
          <Tab eventKey="all" title="Wszystkie">
            <InvoicePaymentsSearch
              getData={getAllData}
              getCount={getAllCount}
              showFilter={true}
              withFilterInColumns={true}
              onRowClick={(id: string) => nav.invoicePaymentEditor(id)}
              refresh={refreshKey > 0}
            />
          </Tab>
        </TabsHistory>
        <Button
          variant="outline-primary"
          onClick={() => nav.invoicePaymentEditor()}
        >
          Nowa wpłata
        </Button>{' '}
        <CsvImportSection
          title="Importuj wpłaty z CSV (mBank)"
          importFunction={(file: File) =>
            spd.invoicePayments.importCsv(file, file.name)
          }
          onImported={() => setRefreshKey(k => k + 1)}
        />
      </Card.Body>
    </Card>
  );
};
